import React from 'react'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import { IcaMediaObject } from '../IcaElements/IcaMediaObject'
import { IcaMeta } from '../IcaElements/IcaMeta'
import { Article } from '../../types/Picklist'
import { Store } from '../../contexts/store-context'
import { IcaKeyValue } from '../IcaElements/IcaKeyValue'
import ItemImage from '../ItemImage/ItemImage'
import Tag from '../Tag/Tag'
import { getSingleSiteMaxQuantity, getTagTypeForStock, MISSING_STORE_NAME, SOLD_SINCE_LAST_RESTOCK } from './picklistHelpers'

interface IItemCardProps {
  item: Article
  sites?: Store[]
  selectedArticles: string[]
  handleSelectArticle: (ean: string) => void
  pickDisplay?: string
  showSingleSiteMaxQuantity: boolean
}

const renderStockInfo = (displayBySold: boolean, stockCount: number, restockedCount: number, soldCount: number, maxQuantityCount: number | undefined) => {
  if (displayBySold) {
    return <span className='font-medium text-black'>{`${soldCount ?? 0} st`}</span>
  }

  const hasMaxQuantity = maxQuantityCount != null

  return <span className='font-medium text-black'>{`${stockCount ?? 0}/${hasMaxQuantity ? maxQuantityCount : restockedCount}${hasMaxQuantity ? '*' : ''}`}</span>
}

const renderStatusTag = (status: string | undefined) => {
  if (!status) return null
  return <span className='justify-self-end'><Tag text={status} type={getTagTypeForStock(status)} /></span>
}

export default function ItemCard({ item, sites, selectedArticles, handleSelectArticle, pickDisplay, showSingleSiteMaxQuantity }: IItemCardProps) {
  const withSites = !!sites && sites.length > 0
  const displayBySold = pickDisplay === SOLD_SINCE_LAST_RESTOCK
  const singleSiteMaxQuantity = getSingleSiteMaxQuantity(
    item.storeAmounts,
    showSingleSiteMaxQuantity,
    withSites
  )
  return (
    <div className={withSites ? 'mb-2' : ''}>
      <IcaCardRow key={item.ean} noninteractive style={{ '--IcaCardRow--grid-template-columns': (!displayBySold && item.stockStatus && !withSites) && '1fr auto auto' } as React.CSSProperties}>
        <div className='flex flex-row items-center gap-2'>
          <IcaCheckbox 
            checked={selectedArticles.includes(item.ean)}
            onChange={() => handleSelectArticle(item.ean)}
          />
          <IcaMediaObject align='center'>
            <ItemImage slot='first' ean={item.ean}/>
            <div className={`flex ${displayBySold ? 'flex-col items-start' : 'items-center gap-2'}`} slot='last'>
              <IcaMeta
                heading={item.name}
                subheading={item.ean}
              />
              {displayBySold && (
                <div className={`flex gap-1 items-center ${item.stockStatus ? 'mt-1' : ''}`}>
                  <IcaKeyValue keytext='Antal i skåp' valuetext={`${item.totalInStock ?? '0'} st`} />
                  {!withSites && item.stockStatus && renderStatusTag(item.stockStatus)}
                </div>
              )}
            </div>
          </IcaMediaObject>
        </div>
        {!displayBySold && !withSites && item.stockStatus && renderStatusTag(item.stockStatus)}
        {renderStockInfo(displayBySold, item.totalInStock ?? 0, item.totalRestocked ?? 0, item.totalSold ?? 0, singleSiteMaxQuantity ?? undefined)}
      </IcaCardRow>

      {withSites && item.storeAmounts?.map((storeAmount) => {
        const storeName = sites.find((site) => site.id === storeAmount.storeId)?.name || MISSING_STORE_NAME
    
        return (
          <IcaCardRow key={storeAmount.storeId} noninteractive style={{ '--IcaCardRow--grid-template-columns': (!displayBySold && storeAmount.status) && 'auto 1fr auto' } as React.CSSProperties}>
            <div className={`flex ${displayBySold ? 'flex-col items-start' : 'items-center gap-1'}`}>
              <IcaMeta
                heading={storeName}
              />
              {displayBySold && <div className={`flex gap-1 items-center ${storeAmount.status ? 'mt-1' : ''}`}>
                <IcaKeyValue keytext='Antal i skåp' valuetext={`${storeAmount.quantityInStockForStore ?? '0'} st`} />
                {storeAmount.status && renderStatusTag(storeAmount.status)}
              </div>}
            </div>
            {!displayBySold && storeAmount.status && renderStatusTag(storeAmount.status)}
            {renderStockInfo(displayBySold, storeAmount.quantityInStockForStore ?? 0, storeAmount.restockedQuantityForStore ?? 0, storeAmount.soldForStore ?? 0, storeAmount.maxQuantityForStore ?? undefined)}
          </IcaCardRow>
        )
      })}
    </div>
  )
}
